import React from "react";
import { useCallback, useRef } from "react";
import socket from "../../socket";
import { getUserContext } from "../../auth";
import { apiFetch } from "../../api";

import SeguimientoDialog from "../components/SeguimientoDialog";

const TOPICOS = ["INICIO", "INFORMACION", "PRECIO", "NEGOCIACION", "SEGUIMIENTO", "INTERES"];
const TOPICO_LABELS = {
  INICIO: "INICIO",
  INFORMACION: "INFORMACIÓN",
  PRECIO: "PRECIO",
  NEGOCIACION: "NEGOCIACIÓN",
  SEGUIMIENTO: "SEGUIMIENTO",
  INTERES: "INTERÉS",
};

const getNombreCliente = (row) =>
  row?.nombreCliente ??
  row?.clienteNombre ??
  row?.cliente?.nombre ??
  row?.nombre ??
  "cliente";

const getVehiculoConsulta = (row) =>
  row?.consulta ??
  row?.vehiculo_consultado ??
  row?.vehiculoConsultado ??
  row?.vehiculo?.modelo ??
  row?.vehiculo ??
  "";

export default function SeguimientoContainer({
  open,
  onClose,
  asignacion,
  adminReadOnly = false,
}) {
  // ---------------------------
  // DEVOLUCIONES (misma lógica)
  // ---------------------------
  const [devItems, setDevItems] = React.useState([]);
  const [devLoading] = React.useState(false);
  const [devSubmitting, setDevSubmitting] = React.useState(false);
  const [texto, setTexto] = React.useState("");
  const [topico, setTopico] = React.useState("");

  const initialCountRef = useRef(null);

  const nombreCliente = getNombreCliente(asignacion);
  const id_ven_cli = asignacion?.id_ven_cli ?? asignacion?.id ?? null;
  const vehiculoConsulta = getVehiculoConsulta(asignacion);
  const estadioActual = asignacion?.estadioActual || "";
  const estadoAsign = asignacion?.estadoVenta ?? asignacion?.estado ?? "ACTIVO";
  const readOnly = adminReadOnly || ["VENTA", "BAJA", "FUTURO"].includes(estadoAsign);

  const topicoOptions = React.useMemo(() => {
    return [
      { value: "", label: "SIN ESTADIO" },
      { value: estadioActual || "", label: estadioActual ? `ESTADIO ACTUAL (${TOPICO_LABELS[estadioActual] || estadioActual})` : "ESTADIO ACTUAL" },
      ...TOPICOS.map((t) => ({ value: t, label: TOPICO_LABELS[t] || t })),
    ];
  }, [estadioActual]);

  const reloadDevoluciones = useCallback(async () => {
    if (!id_ven_cli) return;
    try {
      const response = await apiFetch(`/api/devoluciones/by-asignacion/${id_ven_cli}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDevItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Error recargando devoluciones:", error);
    }
  }, [id_ven_cli]);

  React.useEffect(() => {
    if (!open) return;

    (async () => {
      if (id_ven_cli) {
        try {
          const r = await apiFetch(`/api/devoluciones/by-asignacion/${id_ven_cli}`, {
            credentials: "include",
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const data = await r.json();
          const arr = Array.isArray(data) ? data : [];
          setDevItems(arr);
          initialCountRef.current = arr.length;
        } catch (e) {
          setDevItems([]);
          initialCountRef.current = 0;
        }
      } else {
        initialCountRef.current = 0;
      }
    })();

    const handleCreateDevolucion = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;

      const socketUserId = data.user_id;
      const currentUserId = user?.id;

      if (String(socketUserId) !== String(currentUserId)) {
        reloadDevoluciones();
      }
    };

    socket.on("create_devolucion", handleCreateDevolucion);
    return () => socket.off("create_devolucion", handleCreateDevolucion);
  }, [open, id_ven_cli, reloadDevoluciones, asignacion]);

  const handleSubmitDevolucion = async () => {
    if (readOnly) return;
    if (!id_ven_cli || !texto.trim()) return;

    setDevSubmitting(true);
    try {
      const body = {
        id_ven_cli,
        devolucion: texto.trim(),
        topico: topico || null,
      };
      const res = await apiFetch(`/api/devoluciones/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const nuevo = await res.json();
      setDevItems((prev) => [...prev, nuevo]);

      setTexto("");
      setTopico("");
    } catch (e) {
      console.error("❌ Error creando devolución:", e);
    } finally {
      setDevSubmitting(false);
    }
  };

  // ---------------------------
  // CHECKLIST (misma lógica)
  // ---------------------------
  const [checklist, setChecklist] = React.useState(null);
  const [chkLoading, setChkLoading] = React.useState(false);
  const [fieldLoading, setFieldLoading] = React.useState({});

  const initialSnapshotRef = useRef(null);

  React.useEffect(() => {
    if (!open || !id_ven_cli) return;
    setChkLoading(true);
    apiFetch(`/api/checklist/asignacion/${id_ven_cli}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setChecklist(data);
        initialSnapshotRef.current = data;
      })
      .catch(() => {
        setChecklist(null);
        initialSnapshotRef.current = null;
      })
      .finally(() => setChkLoading(false));
  }, [open, id_ven_cli, asignacion]);

  const handleToggleChecklist = async (campo) => {
    if (readOnly || !id_ven_cli) return;

    setFieldLoading((s) => ({ ...s, [campo]: true }));
    try {
      const res = await apiFetch(`/api/checklist/${id_ven_cli}/toggle/${campo}`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setChecklist(updated);
    } catch (e) {
      console.error(e);
    } finally {
      setFieldLoading((s) => ({ ...s, [campo]: false }));
    }
  };

  // ---------------------------
  // CLOSE (limpia snapshots igual que antes)
  // ---------------------------
  const handleCloseWithLogs = useCallback(() => {
    initialCountRef.current = null;
    initialSnapshotRef.current = null;
    onClose && onClose();
  }, [onClose]);

  return (
    <SeguimientoDialog
      open={open && Boolean(asignacion)}
      onClose={handleCloseWithLogs}
      titulo={`Seguimiento ${nombreCliente}`}
      adminReadOnly={adminReadOnly}
      vehiculo={vehiculoConsulta}
      estado={estadoAsign}
      checklistProps={{
        open: open && Boolean(asignacion),
        checklist,
        loading: chkLoading,
        fieldLoading,
        onToggle: handleToggleChecklist,
        titulo: `Checklist ${nombreCliente}`,
        readOnly,
      }}
      devolucionesProps={{
        open: open && Boolean(asignacion),
        devoluciones: devItems,
        loading: devLoading,
        submitting: devSubmitting,
        onSubmit: handleSubmitDevolucion,
        valueTexto: texto,
        setValueTexto: setTexto,
        valueTopico: topico,
        setValueTopico: setTopico,
        topicoOptions,
        titulo: `Devoluciones ${nombreCliente}`,
        readOnly,
        hideComposer: adminReadOnly,
      }}
    />
  );
}
